import { NRLog } from "@repo/next-utils/newrelic";
import { FourHundredError } from "./four-hundred-error";
import { ServerError, isServerError } from "./server-error";

type Method = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export type ChameleonRequestOptions = {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  requestType?: string;
};

export type ChameleonRequestConstructorOptions = {
  clientName: string;
  clientVersion: string;
  defaultTimeout?: number;
  defaultRetries?: number;
  defaultRetryDelay?: number;
  logger?: (log: NRLog) => void;
  getXSiteOrigin: () => Promise<string | null> | string | null;
};

type ChameleonResponse<T> = {
  data: T;
  responseHeaders: Record<string, string>;
  status: number;
  statusText: string;
};

export class ChameleonRequest {
  private clientName: string;
  private clientVersion: string;
  private timeout: number;
  private retries: number;
  private retryDelay: number;
  private startingRetries: number | undefined;
  private logger?: (log: NRLog) => void;
  private getXSiteOrigin: () => Promise<string | null> | string | null;

  constructor({
    clientName,
    clientVersion,
    defaultTimeout = 15000,
    defaultRetries = 0,
    defaultRetryDelay = 2000,
    logger = undefined,
    getXSiteOrigin,
  }: ChameleonRequestConstructorOptions) {
    this.clientName = clientName;
    this.clientVersion = clientVersion;
    this.timeout = defaultTimeout;
    this.retries = defaultRetries;
    this.retryDelay = defaultRetryDelay;
    this.logger = logger;
    this.startingRetries = undefined;
    this.getXSiteOrigin = getXSiteOrigin;
  }

  private wait = (delay: number) =>
    new Promise((resolve) => setTimeout(resolve, delay));

  private async fetchWithRetry<T>(
    url: string,
    options: RequestInit,
    chameleonOptions: {
      retries: number;
      retryDelay: number;
      requestType?: string;
    } = {
      retries: this.retries,
      retryDelay: this.retryDelay,
    },
  ): Promise<ChameleonResponse<T>> {
    try {
      const time = Date.now();
      const response = await fetch(url, options);
      this.startingRetries = this.startingRetries || chameleonOptions.retries;

      if (this.logger) {
        const requestTypeProperty: { requestType?: string } =
          chameleonOptions?.requestType
            ? { requestType: chameleonOptions.requestType }
            : {};

        this.logger({
          type: "external_requests",
          "request.url": url,
          "response.status": response.status,
          "response.statusText": response.statusText,
          method: options.method as Method,
          timeTaken: Date.now() - time,
          setRetries: this.startingRetries || 0,
          executedRetries: this.startingRetries - chameleonOptions?.retries,
          ...requestTypeProperty,
        });
      }

      if (response.ok) {
        const contentType = response.headers.get("content-type");

        const responseHeaders = Object.fromEntries(response.headers.entries());

        let data: T;

        if (response.status === 204) {
          data = null as T;
        } else if (!contentType || !contentType.includes("application/json")) {
          data = null as T;
        } else {
          data = await response.json();
        }

        return {
          data,
          responseHeaders,
          status: response.status,
          statusText: response.statusText,
        };
      }

      if (response.status >= 400 && response.status < 500) {
        const data = await response.json();
        throw new FourHundredError(response.statusText, response.status, data);
      }

      if (response.status >= 500) {
        throw new ServerError(response.statusText, response.status);
      }

      throw new Error(response.statusText);
    } catch (error) {
      if (error instanceof FourHundredError || chameleonOptions.retries < 1) {
        throw error;
      }

      if (isServerError(error)) {
        await this.wait(chameleonOptions.retryDelay);

        return this.fetchWithRetry(url, options, {
          ...chameleonOptions,
          retries: (chameleonOptions.retries || 0) - 1,
        });
      }

      throw error;
    }
  }

  private amendUrlWithXSiteOrigin = async (url: string): Promise<string> => {
    const xSiteOrigin = await this.getXSiteOrigin();
    if (!xSiteOrigin) {
      return url;
    }

    // Use a dummy base for relative URLs i.e. to handle server and client requests
    const base = url.startsWith("http") ? undefined : "http://localhost";
    const urlObj = new URL(url, base);

    urlObj.searchParams.set("x-site-origin", xSiteOrigin);

    return base ? `${urlObj.pathname}${urlObj.search}` : urlObj.toString();
  };

  public configureRequest<Options extends RequestInit = RequestInit>(
    method: Method,
  ) {
    return async <Response>(
      url: string,
      options?: Options,
      chameleonOptions: ChameleonRequestOptions = {
        timeout: this.timeout,
        retries: this.retries,
        retryDelay: this.retryDelay,
        requestType: undefined,
      },
    ): Promise<ChameleonResponse<Response>> => {
      const controller = new AbortController();

      const timeout = chameleonOptions?.timeout || this.timeout;

      const id = setTimeout(() => {
        controller.abort(`Request timed out after ${timeout}ms`);
      }, timeout);

      const headers = {
        "asos-c-name": this.clientName,
        "asos-c-ver": this.clientVersion,
        "asos-c-plat": "web",
        ...options?.headers,
      };

      // TODO: Decision to either use query parameters or headers for x-site-origin
      url = await this.amendUrlWithXSiteOrigin(url);

      const requestOptions: RequestInit = {
        method,
        ...options,
        headers,
        signal: controller.signal,
      };

      try {
        const retries =
          typeof chameleonOptions.retries === "number"
            ? chameleonOptions.retries
            : this.retries;

        return await this.fetchWithRetry<Response>(url, requestOptions, {
          retries,
          retryDelay: chameleonOptions?.retryDelay || this.retryDelay,
          requestType: chameleonOptions?.requestType,
        });
      } finally {
        clearTimeout(id);
      }
    };
  }

  public get = this.configureRequest("GET");
  public post = this.configureRequest("POST");
  public put = this.configureRequest("PUT");
  public delete = this.configureRequest("DELETE");
  public patch = this.configureRequest("PATCH");
}
